import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Button } from '@mui/material';

const FileUpload = forwardRef(function FileUpload(
  {
    inputId,
    title = 'Attach Files',
    helperText = 'You can select one or more files',
    isUploading = false,
    accept,
    capture,
  },
  ref
) {
  const [files, setFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getFiles: () => files,
    clearFiles: () => setFiles([]),
  }), [files]);

  const updateFiles = (fileList) => {
    const selectedFiles = Array.from(fileList || []);

    setFiles((prevFiles) => {
      const merged = [...prevFiles];
      const existingKeys = new Set(prevFiles.map((file) => `${file.name}-${file.size}-${file.lastModified}`));

      for (const file of selectedFiles) {
        const key = `${file.name}-${file.size}-${file.lastModified}`;
        if (!existingKeys.has(key)) {
          merged.push(file);
          existingKeys.add(key);
        }
      }

      return merged;
    });
  };

  const handleInputChange = (event) => {
    updateFiles(event.target.files);
    event.target.value = '';
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    updateFiles(event.dataTransfer.files);
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        marginBottom: 12,
        padding: '12px 14px',
        border: `1px dashed ${isDragOver ? '#0b426b' : '#8aa0b8'}`,
        borderRadius: 10,
        background: isDragOver ? '#eef6ff' : '#f7fbff',
      }}
    >
      <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>{title}</label>
      <input
        id={inputId}
        ref={fileInputRef}
        type="file"
        multiple
        accept={accept}
        capture={capture}
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />
      <label htmlFor={inputId}>
        <Button
          component="span"
          variant="outlined"
          size="small"
          style={{ textTransform: 'none', fontWeight: 600, borderColor: '#0b426b', color: '#0b426b' }}
        >
          Choose files
        </Button>
      </label>
      <span style={{ marginLeft: 10, color: '#526270', fontSize: '0.9rem' }}>{helperText}</span>
      <div style={{ marginTop: 6, color: '#526270', fontSize: '0.85rem' }}>or drag and drop files here</div>

      {files.length > 0 && (
        <div style={{ marginTop: 8, fontSize: '0.9rem', color: '#22313f' }}>
          {files.length} file(s): {files.map((file) => file.name).join(', ')}
        </div>
      )}

      {isUploading && (
        <div style={{ marginTop: 8, color: '#0b426b', fontWeight: 600 }}>Uploading files...</div>
      )}
    </div>
  );
});

export default FileUpload;
